import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

export interface DropdownOption<TValue = any> {
    label: string;
    value: TValue;
}

@Component({
    selector: 'type-dropdown',
    templateUrl: './type-dropdown.html',
    styleUrls: ['./type-dropdown.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TypeDropdownComponent<TValue = any> {
    // Convert regular inputs to signal inputs
    placeholder = input('Search...');
    options = input<DropdownOption<TValue>[]>([]);
    minChars = input(1);

    @Input()
    value?: TValue;

    @Output()
    valueChange = new EventEmitter<TValue>();

    protected isOpen = signal(false);
    protected query = signal('');

    protected filteredOptions = computed(() => {
        const search = this.query().toLowerCase();
        if (!search || search.length < this.minChars()) {
            return this.options();
        }
        return this.options().filter(option => option.label.toLowerCase().includes(search));
    });

    protected onFocus(): void {
        this.query.set('');
        this.isOpen.set(true);
    }

    protected onBlur(): void {
        setTimeout(() => {
            this.isOpen.set(false);
            this.searchByValue();
        }, 300);
    }

    protected handleClose() {
        this.isOpen.set(false);
    }

    protected selectOption(option: DropdownOption<TValue>): void {
        this.isOpen.set(false);
        this.value = option.value;
        this.valueChange.emit(option.value);
        this.searchByValue();
    }

    protected selectFirstOption(): void {
        const options = this.filteredOptions();
        if (options.length > 0) {
            this.selectOption(options[0]);
        }
    }

    private searchByValue(): void {
        this.query.set(this.options().find(option => option.value === this.value)?.label || '');
    }
}
